import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCart } from '../context/CartContext';

const formatCurrency = (value = 0) =>
  new Intl.NumberFormat('es-PR', { style: 'currency', currency: 'USD' }).format(Number(value || 0));

const FloatingCart = () => {
  const { items, subtotal, taxes, total, removeItem, clearCart } = useCart();
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();

  const toggleCart = () => setOpen((prev) => !prev);

  if (!items.length && !open) {
    return null;
  }

  const handleCheckoutRedirect = () => {
    if (!items.length) return;
    setOpen(false);
    navigate('/checkout');
  };

  return (
    <div className="fixed bottom-4 right-4 z-50">
      <button
        onClick={toggleCart}
        className="rounded-full bg-emerald-600 px-4 py-2 text-sm font-semibold text-white shadow-lg hover:bg-emerald-700"
      >
        {open ? 'Cerrar carrito' : `Carrito (${items.length})`}
      </button>
      {open && (
        <div className="mt-3 w-[320px] rounded-3xl border border-gray-200 bg-white p-4 shadow-2xl">
          <div className="flex items-center justify-between mb-3">
            <h4 className="text-lg font-semibold text-gray-900">Carrito</h4>
            {items.length > 0 && (
              <button
                onClick={clearCart}
                className="text-xs font-semibold uppercase tracking-widest text-red-500 hover:text-red-600"
              >
                Vaciar
              </button>
            )}
          </div>
          {items.length === 0 ? (
            <p className="text-sm text-gray-500">Aún no has agregado productos.</p>
          ) : (
            <div className="space-y-4 max-h-64 overflow-y-auto pr-1">
              {items.map((item) => (
                <div key={item.id} className="rounded-2xl border border-gray-100 p-3">
                  <div className="flex items-center gap-3">
                    {item.image ? (
                      <img src={item.image} alt={item.name} className="h-12 w-12 rounded-xl object-cover" />
                    ) : (
                      <div className="h-12 w-12 rounded-xl bg-gray-100"></div>
                    )}
                    <div className="flex-1">
                      <p className="text-sm font-semibold text-gray-900">{item.name}</p>
                      <p className="text-xs text-gray-500">Base: ${item.basePrice?.toFixed?.(2) || item.basePrice}</p>
                    </div>
                    <button
                      onClick={() => removeItem(item.id)}
                      className="text-xs font-semibold uppercase tracking-widest text-red-500 hover:text-red-600"
                    >
                      Quitar
                    </button>
                  </div>
                  {item.variants?.length > 0 && (
                    <div className="mt-2 text-xs text-gray-600 space-y-1">
                      {item.variants.map((variant) => (
                        <p key={`${item.id}-${variant.attribute}`}>
                          <span className="font-semibold">{variant.attribute}:</span> {variant.value}
                          {variant.priceAdjustment
                            ? ` (${variant.priceAdjustment > 0 ? '+' : ''}${variant.priceAdjustment.toFixed(2)})`
                            : ''}
                        </p>
                      ))}
                    </div>
                  )}
                  {item.addons?.length > 0 && (
                    <div className="mt-1 text-xs text-gray-600">
                      <p className="font-semibold">Add-ons:</p>
                      <ul className="ml-3 list-disc">
                        {item.addons.map((addon) => (
                          <li key={`${item.id}-addon-${addon.name}`}>
                            {(addon.categoryName ? `${addon.categoryName} · ` : '') + addon.name}
                            {addon.quantity ? ` x${addon.quantity}` : ''}
                            {addon.price
                              ? ` (${formatCurrency(addon.price)}${addon.quantity ? ' c/u' : ''})`
                              : ''}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                  <p className="mt-2 text-sm font-semibold text-emerald-700">
                    Total item: ${item.totalPrice?.toFixed?.(2) || item.totalPrice}
                  </p>
                </div>
              ))}
            </div>
          )}
          <div className="mt-4 space-y-1 text-sm text-gray-700">
            <div className="flex justify-between">
              <span>Subtotal</span>
              <span>${subtotal.toFixed(2)}</span>
            </div>
            <div className="flex justify-between">
              <span>Impuestos</span>
              <span>${taxes.toFixed(2)}</span>
            </div>
            <div className="flex justify-between text-base font-semibold text-gray-900">
              <span>Total</span>
              <span>${total.toFixed(2)}</span>
            </div>
          </div>
          <button
            disabled={!items.length}
            onClick={handleCheckoutRedirect}
            className="mt-4 w-full rounded-full bg-gray-900 px-4 py-3 text-sm font-semibold text-white disabled:opacity-50"
          >
            Continuar al checkout
          </button>
        </div>
      )}
    </div>
  );
};

export default FloatingCart;
